import React, { useEffect, useMemo, useState } from 'react';
import NoDataMessage from './NoDataMessage';

import ProductSelector from './ProductSelector';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import CompetitiveSummary from './CompetitiveSummary';

const MIN_MENTIONS = 2;

// Rating thresholds configuration
const RATING_THRESHOLDS = {
    POSITIVE: 0.5,    // Ratings > 0.5 are positive
    NEGATIVE: -0.5,   // Ratings < -0.5 are negative
    // Ratings between NEGATIVE and POSITIVE are neutral
};

/**
 * CompetitiveDifferentiators Component
 * Main component for displaying competitive analysis
 */
const CompetitiveDifferentiators = ({ 
    topics, 
    allPosts, 
    hasTopicData, 
    companyName,
    companyId,
    companyTicker,
    userId,
    competitiveComparisons,
    assessmentData
}) => {
    const [expandedCompetitor, setExpandedCompetitor] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [filterTitle, setFilterTitle] = useState("");
    const [showPostDisplay, setShowPostDisplay] = useState(false);
    const [filteredTopicMap, setFilteredTopicMap] = useState({});

    const ccKeys = Object.keys(competitiveComparisons || {});
    const tIds = topics?.map(t => t.id);
    console.log('[DEBUG] KEY MISMATCH - ACTUAL VALUES:', {
        competitiveComparisonsKey: ccKeys[0],
        topicIds: tIds,
        doAnyMatch: topics?.some(t => !!(competitiveComparisons || {})[t.id])
    });

    // Filter topics to only include those with competitive comparison analyses
    const validTopics = useMemo(() => {
        if (!topics) {
            console.log('[DEBUG] validTopics: topics is null/undefined');
            return [];
        }
        const filtered = topics.filter(topic => {
            const topicComparisons = competitiveComparisons[topic.id];
            const hasCompetitors = topicComparisons && Object.keys(topicComparisons.competitors || {}).length > 0;
            console.log('[DEBUG] validTopics filter check:', {
                topicId: topic.id,
                topicName: topic.topic,
                hasComparisons: !!topicComparisons,
                competitors: topicComparisons?.competitors ? Object.keys(topicComparisons.competitors) : [],
                passes: hasCompetitors
            });
            return hasCompetitors;
        });
        console.log('[DEBUG] validTopics result:', filtered.length, 'topics passed filter');
        return filtered;
    }, [topics, competitiveComparisons]);

    // Filter topics to only include those with competitive comparison assessment data
    useEffect(() => {
        if (!validTopics || !assessmentData) return;
        
        // Group assessmentData by topic_id
        const assessmentsByTopic = {};
        assessmentData.forEach(item => {
            if (item.topic_id) {
                if (!assessmentsByTopic[item.topic_id]) {
                    assessmentsByTopic[item.topic_id] = [];
                }
                assessmentsByTopic[item.topic_id].push(item);
            }
        });
        
        // Filter topics to only include those with assessment data
        const validTopicsWithData = validTopics.filter(topic => 
            assessmentsByTopic[topic.id] && assessmentsByTopic[topic.id].length > 0
        );
        
        // Convert to topicMap format for ProductSelector
        const topicMap = validTopicsWithData.reduce((acc, topic) => {
            acc[topic.id] = {
                id: topic.id,
                name: topic.topic || topic.name,
                priority: topic.priority
            };
            return acc;
        }, {});
        
        setFilteredTopicMap(topicMap);
        
        // Select default product if none is selected and we have topics
        if (!selectedProduct && Object.keys(topicMap).length > 0) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB; // Sort by priority (lowest first)
            });
            
            if (sortedTopics[0]) {
                console.log('Auto-selecting first topic:', sortedTopics[0]);
                setSelectedProduct(sortedTopics[0].id);
            }
        }
    }, [validTopics, assessmentData, selectedProduct]);

    // Reset selected product if it becomes invalid
    useEffect(() => {
        if (selectedProduct && !validTopics.some(t => t.id === selectedProduct)) {
            setSelectedProduct("");
        }
    }, [validTopics, selectedProduct]);

    // Handle back/forward browser navigation
    useEffect(() => {
        const handlePopState = () => clearFilteredPosts();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Get selected topic's assessment data
    const selectedAssessmentData = useMemo(() => {
        if (!selectedProduct || !assessmentData) return assessmentData;
        
        // Filter assessments for selected topic
        return assessmentData.filter(item => 
            item.topic_id === selectedProduct
        );
    }, [selectedProduct, assessmentData]);

    // Get posts for selected product
    const selectedTopicPosts = useMemo(() => {
        if (!selectedProduct || !validTopics) return allPosts;
        const selectedTopic = validTopics.find(t => t.id === selectedProduct);
        return selectedTopic?.posts || allPosts;
    }, [selectedProduct, validTopics, allPosts]);

    // Calculate stats per competitor and total stats
    const { competitorStats, totalStats } = useMemo(() => {
        const stats = {};
        const total = { total: 0, positive: 0, negative: 0, neutral: 0 };
        
        // Initialize market overview stats
        stats['market'] = { total: 0, positive: 0, negative: 0, neutral: 0 };
        
        // Use assessmentData if available
        if (selectedAssessmentData && Array.isArray(selectedAssessmentData)) {
            console.log('Using provided assessment data for competitive differentiators:', selectedAssessmentData.length);
            
            selectedAssessmentData.forEach(assessment => {
                // Use the comparator field instead of details.competitor
                const competitor = assessment.comparator || assessment.details?.competitor || 'market';
                
                // Update total stats
                total.total += 1;
                if (assessment.rating > RATING_THRESHOLDS.POSITIVE) {
                    total.positive += 1;
                } else if (assessment.rating < RATING_THRESHOLDS.NEGATIVE) {
                    total.negative += 1;
                } else {
                    total.neutral += 1;
                }
                
                // Initialize stats for this competitor if not exists
                if (!stats[competitor]) {
                    stats[competitor] = { total: 0, positive: 0, negative: 0, neutral: 0 };
                }
                
                // Update competitor-specific stats
                stats[competitor].total += 1;
                if (assessment.rating > RATING_THRESHOLDS.POSITIVE) {
                    stats[competitor].positive += 1;
                } else if (assessment.rating < RATING_THRESHOLDS.NEGATIVE) {
                    stats[competitor].negative += 1;
                } else {
                    stats[competitor].neutral += 1;
                }
            });
        } 
        // Fall back to post data if no assessment data available
        else {
            console.log('Falling back to posts for competitive differentiators');
            
            selectedTopicPosts?.forEach(post => {
                post?.post_assessments?.forEach(assessment => {
                    if (assessment?.type === 'competitive_comparison') {
                        const competitor = assessment.details?.competitor || 'market';
                        
                        // Update total stats
                        total.total += 1;
                        if (assessment.rating > RATING_THRESHOLDS.POSITIVE) {
                            total.positive += 1;
                        } else if (assessment.rating < RATING_THRESHOLDS.NEGATIVE) {
                            total.negative += 1;
                        } else {
                            total.neutral += 1;
                        }
                        
                        // Initialize stats for this competitor if not exists
                        if (!stats[competitor]) {
                            stats[competitor] = { total: 0, positive: 0, negative: 0, neutral: 0 };
                        }
                        
                        // Update competitor-specific stats
                        stats[competitor].total += 1;
                        if (assessment.rating > RATING_THRESHOLDS.POSITIVE) {
                            stats[competitor].positive += 1;
                        } else if (assessment.rating < RATING_THRESHOLDS.NEGATIVE) {
                            stats[competitor].negative += 1;
                        } else {
                            stats[competitor].neutral += 1;
                        }
                    }
                });
            });
        }

        return { competitorStats: stats, totalStats: total };
    }, [selectedTopicPosts, selectedAssessmentData]);

    // Get market overview data
    const marketOverview = useMemo(() => {
        console.log('Computing marketOverview from:', {
            selectedProduct,
            validTopics: validTopics?.map(t => ({ id: t.id, topic: t.topic })),
            competitiveComparisons: Object.keys(competitiveComparisons || {})
        });

        if (!selectedProduct) {
            let overview = null;
            validTopics?.forEach(topic => {
                console.log('Checking topic for market overview:', {
                    topicId: topic.id,
                    topicName: topic.topic,
                    competitors: competitiveComparisons[topic.id]?.competitors
                });
                const topicComparisons = competitiveComparisons[topic.id];
                if (topicComparisons?.competitors) {
                    // First check for _self data
                    if (topicComparisons.competitors['_self']) {
                        overview = {
                            summary: topicComparisons.competitors['_self'].summary,
                            text: topicComparisons.competitors['_self'].details,
                            topicName: topic.topic
                        };
                    } else {
                        // Fallback to first competitive comparison if no _self data
                        Object.entries(topicComparisons.competitors).forEach(([_, data]) => {
                            if (!overview && data.relationship_type === 'competitive_comparison') {
                                overview = {
                                    summary: data.summary,
                                    text: data.details,
                                    topicName: topic.topic
                                };
                            }
                        });
                    }
                }
            });
            return overview;
        }

        const selectedTopic = validTopics?.find(t => t.id === selectedProduct);
        if (!selectedTopic) return null;

        const topicComparisons = competitiveComparisons[selectedProduct];
        if (!topicComparisons?.competitors) return null;

        // First try to get _self data for the selected product
        if (topicComparisons.competitors['_self']) {
            return {
                summary: topicComparisons.competitors['_self'].summary,
                text: topicComparisons.competitors['_self'].details,
                topicName: selectedTopic.topic
            };
        }

        // Fallback to first competitive comparison
        const overview = Object.entries(topicComparisons.competitors)
            .find(([_, data]) => data.relationship_type === 'competitive_comparison');

        return overview ? {
            summary: overview[1].summary,
            text: overview[1].details,
            topicName: selectedTopic.topic
        } : null;
    }, [validTopics, selectedProduct, competitiveComparisons]);

    // Get competitor details
    const competitorDetails = useMemo(() => {
        const details = {};
        validTopics?.forEach(topic => {
            const topicComparisons = competitiveComparisons[topic.id];
            if (topicComparisons?.competitors) {
                Object.entries(topicComparisons.competitors).forEach(([competitor, data]) => {
                    // Skip _self entries as they belong in market overview
                    if (competitor !== '_self' && data.relationship_type === 'competitive_comparison') {
                        details[competitor] = {
                            summary: data.summary,
                            text: data.details,
                            topic: topic.topic
                        };
                    }
                });
            }
        });
        return details;
    }, [validTopics, competitiveComparisons]);

    // Sort competitors by total mentions and filter by minimum mentions
    const sortedCompetitors = useMemo(() => {
        console.log('[DEBUG] sortedCompetitors input:', {
            competitorDetails: Object.keys(competitorDetails),
            competitorStats
        });
        const result = Object.entries(competitorDetails)
            .map(([competitor, details]) => ({
                competitor,
                details,
                stats: competitorStats[competitor] || { total: 0 }
            }))
            .filter(({ stats }) => stats.total >= MIN_MENTIONS)
            .sort((a, b) => b.stats.total - a.stats.total);
        console.log('[DEBUG] sortedCompetitors result:', result.length, 'competitors after MIN_MENTIONS filter');
        return result;
    }, [competitorDetails, competitorStats]);

    const handleToggle = (competitor) => {
        setExpandedCompetitor(expandedCompetitor === competitor ? null : competitor);
    };

    const handleSegmentClick = (segmentType, competitor = null) => {
        console.log('=== handleSegmentClick START ===', {
            segmentType,
            competitor,
            selectedProduct,
            selectedTopicName: getSelectedProductName(),
            topicMap: filteredTopicMap,
            competitiveComparisons: competitiveComparisons,
            marketOverview: marketOverview,
            selectedAssessmentData: {
                length: selectedAssessmentData?.length,
                sample: selectedAssessmentData?.[0],
                first5: selectedAssessmentData?.slice(0, 5)
            }
        });
        
        const selectedTopicName = getSelectedProductName();
        const segmentFilteredPosts = filterPostsBySegment(segmentType, competitor, selectedTopicPosts, selectedAssessmentData);
        
        console.log('=== handleSegmentClick FILTERED POSTS ===', {
            segmentType,
            competitor,
            filteredCount: segmentFilteredPosts?.length,
            filteredPosts: segmentFilteredPosts,
            selectedTopicName
        });

        const title = generateFilterTitle(segmentType, competitor, selectedTopicName, segmentFilteredPosts.length);

        // Update UI state
        window.history.pushState({ filteredPostsView: true }, '', window.location.pathname + window.location.search);
        setFilteredPosts(segmentFilteredPosts);
        setFilterTitle(title);
        setShowPostDisplay(true);
    };

    const clearFilteredPosts = () => {
        setFilteredPosts([]);
        setFilterTitle("");
        setShowPostDisplay(false);
    };

    const getSelectedProductName = () => {
        if (!selectedProduct) return companyName;
        const selectedTopic = validTopics.find(t => t.id === selectedProduct);
        return selectedTopic?.topic || companyName;
    };

    // Early return if no competitive data
    if (!Object.keys(competitiveComparisons).length) {
        console.log('[DEBUG] EARLY RETURN - competitiveComparisons is empty:', competitiveComparisons);
        return <NoDataMessage message="No competitive differentiator data available." />;
    }
    console.log('[DEBUG] Passed early return check, rendering main view with:', {
        marketOverview: !!marketOverview,
        sortedCompetitorsCount: sortedCompetitors.length,
        filteredTopicMapKeys: Object.keys(filteredTopicMap),
        selectedProduct
    });

    // Render main view
    return (
        <>
            <div className="content-section">
                {/* <div className="content-header">
                    <h2>Competitive Differentiators</h2>
                </div> */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    {Object.keys(filteredTopicMap).length > 0 && (
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct}
                            onSelect={setSelectedProduct}
                            className="product-selector"
                            label="Product"
                        />
                    )}
                </div>

                <div className="competitor-grid">
                    {marketOverview && (
                        <CompetitiveSummary
                            key="market"
                            competitor="Market Overview"
                            stats={totalStats}
                            expanded={expandedCompetitor === 'market'}
                            onToggle={() => handleToggle('market')}
                            companyId={companyId}
                            summary={marketOverview.summary}
                            competitorDetails={{ 'Market Overview': { text: marketOverview.text } }}
                            topicName={getSelectedProductName()}
                            onSegmentClick={handleSegmentClick}
                        />
                    )}
                    {sortedCompetitors.map(({ competitor, details, stats }) => (
                        <CompetitiveSummary
                            key={competitor}
                            competitor={competitor}
                            stats={stats}
                            expanded={expandedCompetitor === competitor}
                            onToggle={() => handleToggle(competitor)}
                            companyId={companyId}
                            summary={details.summary}
                            competitorDetails={competitorDetails}
                            topicName={getSelectedProductName()}
                            onSegmentClick={handleSegmentClick}
                        />
                    ))}
                </div>
            </div>
            {showPostDisplay && filteredPosts && filteredPosts.length > 0 && (
                <div className="filtered-posts-container" style={{ marginTop: '20px' }}>
                    <FilteredPostsDisplay
                        title={filterTitle}
                        posts={filteredPosts}
                        onClose={() => {
                            setShowPostDisplay(false);
                            setFilteredPosts([]);
                            setFilterTitle("");
                        }}
                        showBookmarkButton={true}
                        ratingDisplayName="Competitive Comparison"
                        id="competitive-filtered-posts"
                        ratingColorScheme={{ 
                            high: RATING_THRESHOLDS.POSITIVE, 
                            low: RATING_THRESHOLDS.NEGATIVE 
                        }}
                        hideHeader={false}
                        showCloseButton={true}
                        autoScroll={true}
                    />
                </div>
            )}
        </>
    );
};

// Helper functions
const filterPostsBySegment = (segmentType, competitor, posts, assessmentData) => {
    console.log('=== filterPostsBySegment START ===', {
        segmentType,
        competitor,
        postsLength: posts?.length,
        assessmentDataLength: assessmentData?.length,
        assessmentDataSample: assessmentData?.[0],
        first5Assessments: assessmentData?.slice(0, 5)
    });

    // Use assessment data if available
    if (assessmentData && Array.isArray(assessmentData)) {
        const filtered = assessmentData
            .filter(item => {
                if (!item) {
                    console.log('Skipping null/undefined item');
                    return false;
                }
                
                const itemCompetitor = item.comparator || item.details?.competitor;
                const rating = parseFloat(item.rating);
                
                console.log('Processing assessment item:', {
                    itemCompetitor,
                    rating,
                    targetCompetitor: competitor,
                    isMarketOverview: competitor === "Market Overview",
                    hasSpecificCompetitor: !!itemCompetitor,
                    item: item
                });

                // For Market Overview, include items that:
                // - Have no competitor specified
                // - Have "_self" as competitor
                // - Have "market" as competitor
                // - Have any competitor (since this represents market-wide sentiment)
                if (competitor === "Market Overview") {
                    const isMarketOverviewItem = !itemCompetitor || 
                                               itemCompetitor === "_self" || 
                                               itemCompetitor === "market" ||
                                               true; // Include all items for market overview
                    console.log('Market Overview check:', {
                        isMarketOverviewItem,
                        itemCompetitor,
                        item,
                        rating: item.rating
                    });
                    if (!isMarketOverviewItem) {
                        return false;
                    }
                } else if (competitor && itemCompetitor !== competitor) {
                    console.log('Competitor mismatch:', {
                        itemCompetitor,
                        targetCompetitor: competitor
                    });
                    return false;
                }
                
                if (isNaN(rating)) {
                    console.log('Invalid rating value:', rating);
                    return false;
                }
                
                let result;
                switch (segmentType) {
                    case 'positive':
                        result = rating > RATING_THRESHOLDS.POSITIVE;
                        break;
                    case 'neutral':
                        result = rating >= RATING_THRESHOLDS.NEGATIVE && 
                                rating <= RATING_THRESHOLDS.POSITIVE;
                        break;
                    case 'negative':
                        result = rating < RATING_THRESHOLDS.NEGATIVE;
                        break;
                    default:
                        result = false;
                }
                console.log(`Rating check for ${segmentType} (${rating}):`, result);
                return result;
            })
            .map(item => {
                const itemCompetitor = item.comparator || item.details?.competitor;
                const mappedItem = {
                    ...item,
                    post_id: item.post_id || item.id,
                    time: item.time || item.created_at,
                    text: item.text || item.content,
                    title: item.title,
                    url: item.url,
                    post_assessments: [{
                        type: 'competitive_comparison',
                        rating: parseFloat(item.rating),
                        confidence: item.confidence,
                        explanation: item.explanation,
                        details: {
                            competitor: competitor === "Market Overview" ? "_self" : itemCompetitor
                        }
                    }],
                    ratingName: 'Competitive Comparison',
                    ratingType: 'competitive_comparison'
                };
                console.log('Mapped assessment item:', mappedItem);
                return mappedItem;
            });
            
        console.log('=== filterPostsBySegment RESULTS ===', {
            segmentType,
            competitor,
            filteredCount: filtered?.length,
            filteredItems: filtered
        });
        return filtered;
    }
    
    // Fall back to processing posts
    console.log('Falling back to processing posts');
    return posts
        .filter(post => {
            return post.post_assessments?.some(assessment => {
                if (assessment.type !== 'competitive_comparison') return false;
                
                const assessmentCompetitor = assessment.details?.competitor;
                const rating = parseFloat(assessment.rating);
                
                console.log('Processing post assessment:', {
                    assessmentCompetitor,
                    rating,
                    targetCompetitor: competitor,
                    isMarketOverview: competitor === "Market Overview",
                    assessment
                });

                if (competitor === "Market Overview") {
                    const isMarketOverviewPost = !assessmentCompetitor || 
                                               assessmentCompetitor === "_self" || 
                                               assessmentCompetitor === "market";
                    console.log('Market Overview post check:', {
                        isMarketOverviewPost,
                        assessmentCompetitor,
                        assessment
                    });
                    if (!isMarketOverviewPost) {
                        return false;
                    }
                } else if (competitor && assessmentCompetitor !== competitor) {
                    console.log('Competitor mismatch in post:', {
                        assessmentCompetitor,
                        targetCompetitor: competitor
                    });
                    return false;
                }
                
                if (isNaN(rating)) {
                    console.log('Invalid rating value in post:', rating);
                    return false;
                }
                
                switch (segmentType) {
                    case 'positive':
                        return rating > RATING_THRESHOLDS.POSITIVE;
                    case 'neutral':
                        return rating >= RATING_THRESHOLDS.NEGATIVE && 
                               rating <= RATING_THRESHOLDS.POSITIVE;
                    case 'negative':
                        return rating < RATING_THRESHOLDS.NEGATIVE;
                    default:
                        return false;
                }
            });
        })
        .map(post => {
            const assessment = post.post_assessments.find(a => 
                a.type === 'competitive_comparison' && 
                (competitor === "Market Overview" ? 
                    (!a.details?.competitor || ["_self", "market"].includes(a.details?.competitor)) :
                    (!competitor || a.details?.competitor === competitor))
            );
            const mappedPost = {
                ...post,
                rating: parseFloat(assessment?.rating),
                confidence: assessment?.confidence,
                explanation: assessment?.explanation,
                ratingName: 'Competitive Comparison',
                ratingType: 'competitive_comparison'
            };
            console.log('Mapped post:', mappedPost);
            return mappedPost;
        });
};

const generateFilterTitle = (segmentType, competitor, productName, count) => {
    if (competitor === "Market Overview") {
        return `${count} users ${
            segmentType === 'positive' ? `prefer ${productName} over alternatives` :
            segmentType === 'neutral' ? `are neutral between ${productName} and alternatives` :
            `prefer alternatives over ${productName}`
        }`;
    }
    
    if (competitor) {
        return `${count} users ${
            segmentType === 'positive' ? `prefer ${productName} over ${competitor}` :
            segmentType === 'neutral' ? `are neutral between ${competitor} and ${productName}` :
            `prefer ${competitor} over ${productName}`
        }`;
    }
    
    return `${count} users ${
        segmentType === 'positive' ? `prefer ${productName} over the market` :
        segmentType === 'neutral' ? `are neutral between ${productName} and the market` :
        `prefer the market over ${productName}`
    }`;
};

export default CompetitiveDifferentiators;